// Sign-in and other single-task pages before you're in (DESIGN.md: layouts). Adapted from the
// shadcn/ui block login-04 (form beside an image): the form on a card, beside the club's
// photo or, until there is one, the contour placeholder with the pennant. On phones the
// picture drops away and the form fills the screen.

import { Contour } from "@union/ui/components/contour";
import { Pennant } from "@union/ui/components/pennant";
import { cn } from "@union/ui/lib/utils";
import type { ReactNode } from "react";

export function AuthLayout({
  eyebrow,
  title,
  description,
  children,
  footer,
  image,
  className,
}: {
  /** Whose page this is ("Union Clubs", or the club's name). */
  eyebrow: string;
  /** What to do here ("Sign in to your clubs"). */
  title: string;
  description?: ReactNode;
  /** The form. */
  children: ReactNode;
  /** Quiet help under the card ("Trouble signing in? Ask your pro shop."). */
  footer?: ReactNode;
  /** The club's own photo; omit for the contour placeholder (never stock imagery). */
  image?: { src: string; alt: string };
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-svh flex-col items-center justify-center bg-background p-space-4 md:p-space-7",
        className,
      )}
    >
      <div className="flex w-full max-w-dialog-narrow flex-col gap-space-4 md:max-w-auth">
        <div className="grid overflow-hidden rounded-lg border border-border bg-card md:grid-cols-2">
          <div className="flex flex-col gap-space-5 p-space-5 md:p-space-7">
            <div className="grid gap-space-1">
              <p className="type-eyebrow text-muted-foreground">{eyebrow}</p>
              <h1 className="type-heading">{title}</h1>
              {description && <p className="type-body text-muted-foreground">{description}</p>}
            </div>
            {children}
          </div>
          <div className="relative hidden min-h-80 bg-primary-soft md:block">
            {image ? (
              <img src={image.src} alt={image.alt} className="absolute inset-0 size-full object-cover" />
            ) : (
              <>
                <Contour className="text-primary-text opacity-45" />
                <Pennant size="lg" className="absolute top-[34%] left-[62%]" />
              </>
            )}
          </div>
        </div>
        {footer && <div className="text-center type-caption text-muted-foreground">{footer}</div>}
      </div>
    </div>
  );
}
